/**
 * workOutcomeVocabulary — the Product Visual System V2 four-outcome vocabulary
 * (FULL-0099 / PROD-UX-013, MASTER_FLOW §14C.1).
 *
 * §14C.1 asks for "reusable status/state vocabulary" and for "shared design tokens and reusable
 * components rather than page-specific styling drift". `statusVocabulary` owns the operator chip
 * scale; this module owns the one reading of **a unit of Digital Employee work**: the four states
 * an operator has to be able to tell apart without reading logs —
 *
 *     active     the work is in progress now
 *     waiting    the work is parked on a person or a customer
 *     blocked    the work cannot proceed
 *     completed  the work finished successfully
 *
 * Every employee surface that shows a run's state renders it through this map and the one chip
 * (`WorkOutcomeChip`), so the same stored status cannot read as "בעבודה" on one screen and
 * something else on the next. `WORK_OUTCOME_META` borrows its tones from `statusVocabulary`, so the
 * four outcomes are four arms of the shared visual system rather than a second palette.
 *
 * THE SCALE. `AgentRun.status` is a closed 15-arm native Postgres enum
 * (`backend/app/models/agent_run.py::AgentRunStatus`), so the partition is exactly checkable and
 * `RUN_STATUS_WORK_OUTCOME` is total over it. `workOutcomeVocabulary.test.js` parses the arms out of
 * the Python source and asserts this map's key set EQUALS them, so a renamed or added arm reddens
 * the suite instead of falling through to an unlabelled chip.
 *
 * A value outside the scale is NOT guessed. `workOutcomeForRunStatus` returns null and
 * `workOutcomeMeta` answers with `statusVocabulary`'s explicit `unknown` arm — the same rule
 * `runStatusVocabulary` and `statusChipMeta` already hold, and the reason this module is safe to
 * render on a build that is behind the producer.
 *
 * `approved` — the one arm the producer classifies two ways, so the choice is recorded here rather
 * than left implicit in whichever screen happened to draw it:
 *
 *   - it is ACTIVE here. Two independent producers answer the current-state question that way:
 *     `conversation_service` narrates it to the operator as "אושר — מבצע/ת עכשיו" and keeps it out
 *     of `_RUN_TERMINAL_STAGES`, and `TicketEmployeeWorkbench.nextAction` carves it out of its own
 *     `DONE_STATUSES` into `tone: "active"`.
 *   - the counter-signal, named so it is not lost: `agent_runs`' statistics triple
 *     (`_terminal = [COMPLETED, RESOLVED, APPROVED]`, `_RATEABLE_STATUSES`) counts it as a
 *     successful *outcome* for reporting, and `TicketEmployeeWorkbench.STATUS_CHIP` drew it with
 *     the done tone — which contradicted that same file's next-action ladder.
 *   - what is NOT in dispute: `_ACTIVE_RUN_STATUSES` and `working_run_statuses()` exclude it, so
 *     this reading never claims workload the routing desk does not count.
 *
 * Reading it as ACTIVE is the reading that keeps a run whose execution is in flight from being
 * presented to an operator as finished. The arm's own word (`runStatusLabel`) is still drawn beside
 * the chip wherever a status word was already shown, so nothing about the record is concealed.
 */
import { LIVE_STATUS_CLS, LIVE_STATUS_META } from "./statusVocabulary";

/** The four §14C.1 work outcomes. */
export const WORK_OUTCOMES = {
  active: "active",
  waiting: "waiting",
  blocked: "blocked",
  completed: "completed",
};

/** Reading order: what is happening now, then what is stuck, then what is done. */
export const WORK_OUTCOME_ORDER = [
  WORK_OUTCOMES.active,
  WORK_OUTCOMES.waiting,
  WORK_OUTCOMES.blocked,
  WORK_OUTCOMES.completed,
];

// `vocabulary` names the shared statusVocabulary arm whose tone this outcome draws, so the tone's
// owner stays visible from here instead of the class string being copied a second time.
export const WORK_OUTCOME_META = {
  active: {
    key: WORK_OUTCOMES.active,
    label: "בעבודה",
    vocabulary: "working",
    cls: LIVE_STATUS_CLS.working,
  },
  waiting: {
    key: WORK_OUTCOMES.waiting,
    label: "ממתין",
    vocabulary: "waiting_human",
    cls: LIVE_STATUS_CLS.waiting_human,
  },
  blocked: {
    key: WORK_OUTCOMES.blocked,
    label: "חסום",
    vocabulary: "blocked",
    cls: LIVE_STATUS_CLS.blocked,
  },
  completed: {
    key: WORK_OUTCOMES.completed,
    label: "הושלם",
    vocabulary: "completed",
    cls: LIVE_STATUS_CLS.completed,
  },
};

/**
 * Every arm of `AgentRunStatus`, each mapped to exactly one outcome. The key set is asserted equal
 * to the parsed enum by the test beside this module.
 */
export const RUN_STATUS_WORK_OUTCOME = {
  // in progress — the arms `employee_workspace.working_run_statuses()` calls "working", plus
  // `approved` (see the module note above).
  pending: WORK_OUTCOMES.active,
  analyzing: WORK_OUTCOMES.active,
  diagnosing: WORK_OUTCOMES.active,
  executing: WORK_OUTCOMES.active,
  connection_active: WORK_OUTCOMES.active,
  approved: WORK_OUTCOMES.active,
  // parked on a person or a customer
  awaiting_technician_review: WORK_OUTCOMES.waiting,
  awaiting_approval: WORK_OUTCOMES.waiting,
  waiting_for_customer_connection: WORK_OUTCOMES.waiting,
  waiting_for_customer_answer: WORK_OUTCOMES.waiting,
  paused: WORK_OUTCOMES.waiting,
  // cannot proceed
  failed: WORK_OUTCOMES.blocked,
  rejected: WORK_OUTCOMES.blocked,
  // finished successfully
  completed: WORK_OUTCOMES.completed,
  resolved: WORK_OUTCOMES.completed,
};

/**
 * The outcome of a stored run status, or null when the value is outside the producer's scale.
 * `hasOwnProperty` rather than a bare lookup: the object literal above has `Object.prototype` in its
 * chain, so `RUN_STATUS_WORK_OUTCOME["constructor"]` would otherwise answer with a function that a
 * truthiness check would treat as a real outcome.
 */
export function workOutcomeForRunStatus(value) {
  if (typeof value !== "string") return null;
  return Object.prototype.hasOwnProperty.call(RUN_STATUS_WORK_OUTCOME, value)
    ? RUN_STATUS_WORK_OUTCOME[value]
    : null;
}

/**
 * The label + tone for an outcome key. Anything that is not one of the four — including the null
 * `workOutcomeForRunStatus` returns for an arm this build does not know — reads as the shared
 * explicit `unknown` chip, never as a guessed outcome.
 */
export function workOutcomeMeta(key) {
  if (typeof key === "string" && Object.prototype.hasOwnProperty.call(WORK_OUTCOME_META, key)) {
    return WORK_OUTCOME_META[key];
  }
  return {
    key: null,
    label: LIVE_STATUS_META.unknown.label,
    vocabulary: "unknown",
    cls: LIVE_STATUS_META.unknown.cls,
  };
}

/** True when the value is one of the four outcomes this build can present. */
export const isKnownWorkOutcome = (key) =>
  typeof key === "string" && Object.prototype.hasOwnProperty.call(WORK_OUTCOME_META, key);
